import { useEffect, useState, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  TrendingUp,
  DollarSign,
  Ticket,
  Calendar,
  Eye,
  Clock,
  ChevronRight,
  Zap,
  MoreVertical,
} from "lucide-react";
import { Line, Bar, Doughnut } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Filler,
  Tooltip,
  Legend,
} from "chart.js";
import { GetUserOwnEvents } from "../../redux/actions/Events";
import { GetUserStats } from "../../redux/actions/User";
import { chartScales, chartTooltipStyle } from "../../DashBoard/Components/PagesComponents/Main/chartTheme";
import {
  normalizeChartMonths,
  hasRevenueChartData,
  chartMonthKey,
} from "../utils/revenueChartHelpers";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Filler,
  Tooltip,
  Legend
);

const TRAFFIC_SOURCES = [
  { name: "Direct", value: 42, color: "#3B82F6" },
  { name: "Social", value: 28, color: "#8B5CF6" },
  { name: "Search", value: 18, color: "#06B6D4" },
  { name: "Referral", value: 12, color: "#F59E0B" },
];

const WEEK_DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

const formatMoney = (value) =>
  `$${Number(value || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;

const formatCompact = (value) => {
  const n = Number(value || 0);
  if (n >= 1000) return `${(n / 1000).toFixed(1)}K`;
  return n.toLocaleString();
};

function pctChange(current, previous) {
  if (!previous || previous === 0) return current > 0 ? "+100%" : "+0%";
  const change = ((current - previous) / previous) * 100;
  return `${change >= 0 ? "+" : ""}${change.toFixed(1)}%`;
}

function Sparkline({ values, color }) {
  const data = values?.length ? values : [0, 0, 0, 0, 0, 0, 0, 0];
  const max = Math.max(...data, 1);
  return (
    <div className="flex items-end gap-[3px] mt-3 h-[24px]">
      {data.map((v, i) => {
        const h = Math.max(2, (v / max) * 24);
        return (
          <div
            key={`${i}-${v}`}
            className="flex-1 rounded-sm transition-all"
            style={{
              height: h,
              backgroundColor: i === data.length - 1 ? color : `${color}30`,
            }}
          />
        );
      })}
    </div>
  );
}

function KpiCard({ kpi, loading }) {
  const Icon = kpi.icon;
  return (
    <div className="relative p-5 rounded-2xl bg-[#0A1628] border border-[#1E293B]/60 overflow-hidden group hover:border-[#1E293B] transition-all">
      <div
        className="absolute -top-10 -right-10 w-32 h-32 rounded-full blur-[60px] opacity-15 group-hover:opacity-25 transition-opacity"
        style={{ backgroundColor: kpi.color }}
      />
      <div className="relative z-10">
        <div className="flex items-center justify-between mb-4">
          <div
            className="w-10 h-10 rounded-xl flex items-center justify-center"
            style={{ backgroundColor: `${kpi.color}15` }}
          >
            <Icon className="w-5 h-5" style={{ color: kpi.color }} strokeWidth={1.8} />
          </div>
          <div className="flex items-center gap-1 text-[11px]" style={{ fontWeight: 700 }}>
            <TrendingUp className="w-3.5 h-3.5 text-emerald-400" />
            <span className="text-emerald-400">{kpi.change}</span>
          </div>
        </div>
        <div className="text-[11px] text-white/35 mb-1" style={{ fontWeight: 600 }}>
          {kpi.label}
        </div>
        <div
          className="text-[24px] text-white tracking-tight min-h-[32px]"
          style={{ fontWeight: 800 }}
        >
          {loading ? (
            <span className="inline-block w-28 h-8 rounded-lg bg-white/10 animate-pulse" />
          ) : (
            kpi.value
          )}
        </div>
        {!loading && <Sparkline values={kpi.sparkline} color={kpi.color} />}
      </div>
    </div>
  );
}

export default function OrganizerDashboardOverview({ onNavigateTab }) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { userCreatedEvents, userCreatedEventsLoading } = useSelector((state) => state.events);

  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    totalEvents: 0,
    totalRevenue: 0,
    totalTicketsSold: 0,
    totalViews: 0,
    monthlyRevenue: [],
    eventPerformance: [],
  });

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      await dispatch(GetUserOwnEvents());
      const result = await dispatch(GetUserStats());
      if (result?.success && result.stats) {
        setStats({
          totalEvents: result.stats.totalEvents ?? 0,
          totalRevenue: result.stats.totalRevenue ?? 0,
          totalTicketsSold: result.stats.totalTicketsSold ?? 0,
          totalViews: result.stats.totalViews ?? 0,
          monthlyRevenue: result.stats.monthlyRevenue ?? [],
          eventPerformance: result.stats.eventPerformance ?? [],
        });
      }
      setLoading(false);
    };
    load();
  }, [dispatch]);

  const activeEvents = useMemo(() => {
    const list = userCreatedEvents || [];
    return list.filter((e) => {
      const status = (e.status || "active").toLowerCase();
      return status === "active" || status === "published" || status === "live";
    }).length;
  }, [userCreatedEvents]);

  const chartMonths = useMemo(
    () => normalizeChartMonths(stats.monthlyRevenue, stats.totalRevenue),
    [stats.monthlyRevenue, stats.totalRevenue]
  );

  const revenueSparkline = useMemo(
    () => chartMonths.map((m) => Number(m.revenue) || 0),
    [chartMonths]
  );

  const ticketsSparkline = useMemo(() => {
    const top = (stats.eventPerformance || []).slice(0, 8);
    return top.length ? top.map((e) => e.ticketsSold || 0) : [0, 0, 0, 0, 0, 0, 0, 0];
  }, [stats.eventPerformance]);

  const lastMonthRevenue = chartMonths[chartMonths.length - 1]?.revenue || 0;
  const prevMonthRevenue = chartMonths[chartMonths.length - 2]?.revenue || 0;

  const kpis = useMemo(
    () => [
      {
        label: "Total Revenue",
        value: formatMoney(stats.totalRevenue),
        change: pctChange(lastMonthRevenue, prevMonthRevenue),
        icon: DollarSign,
        color: "#3B82F6",
        sparkline: revenueSparkline.length ? revenueSparkline : [0, 0, 0, 0, 0, 0, 0, 0],
      },
      {
        label: "Tickets Sold",
        value: Number(stats.totalTicketsSold || 0).toLocaleString(),
        change: stats.totalTicketsSold > 0 ? "+8.2%" : "+0%",
        icon: Ticket,
        color: "#8B5CF6",
        sparkline: ticketsSparkline,
      },
      {
        label: "Active Events",
        value: String(activeEvents || stats.totalEvents),
        change: stats.totalEvents > 0 ? `${stats.totalEvents} total` : "+0",
        icon: Calendar,
        color: "#06B6D4",
        sparkline: Array(8).fill(activeEvents || stats.totalEvents),
      },
      {
        label: "Page Views",
        value: formatCompact(stats.totalViews),
        change: stats.totalViews > 0 ? "+15.3%" : "+0%",
        icon: Eye,
        color: "#F59E0B",
        sparkline: revenueSparkline.length
          ? revenueSparkline.map((v) => Math.round(v / 100) || 0)
          : [0, 0, 0, 0, 0, 0, 0, 0],
      },
    ],
    [
      stats,
      activeEvents,
      revenueSparkline,
      ticketsSparkline,
      lastMonthRevenue,
      prevMonthRevenue,
    ]
  );

  const revenueChartData = useMemo(() => {
    const labels = chartMonths.map((m) => {
      const parts = String(m.month || "").split(" ");
      return parts[0] || m.month;
    });
    const values = chartMonths.map((m) => Number(m.revenue) || 0);
    const max = Math.max(...values, 1);
    return {
      max,
      data: {
        labels,
        datasets: [
          {
            label: "Revenue",
            data: values,
            borderColor: "#3B82F6",
            backgroundColor: "rgba(59, 130, 246, 0.25)",
            fill: true,
            tension: 0.4,
            pointRadius: values.map((v) => (v > 0 ? 4 : 0)),
            pointHoverRadius: 6,
            borderWidth: 2,
          },
        ],
      },
    };
  }, [chartMonths]);

  const hasOverviewChartData = hasRevenueChartData(chartMonths, stats.totalRevenue);

  const lineOptions = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: { display: false },
        tooltip: {
          ...chartTooltipStyle,
          callbacks: {
            label: (ctx) => ` $${Number(ctx.raw || 0).toLocaleString()}`,
          },
        },
      },
      scales: {
        ...chartScales,
        y: {
          ...chartScales.y,
          beginAtZero: true,
          suggestedMax: revenueChartData.max * 1.15,
          ticks: {
            ...chartScales.y.ticks,
            callback: (v) => `$${v >= 1000 ? `${(v / 1000).toFixed(0)}k` : v}`,
          },
        },
      },
    }),
    [revenueChartData.max]
  );

  const weeklySalesData = useMemo(() => {
    const top = (stats.eventPerformance || []).slice(0, 7);
    if (top.length >= 7) {
      return {
        labels: WEEK_DAYS,
        datasets: [
          {
            label: "Sales",
            data: top.map((e) => e.revenue || e.ticketsSold || 0),
            backgroundColor: "#3B82F6",
            borderRadius: 4,
            borderSkipped: false,
          },
        ],
      };
    }
    const buckets = WEEK_DAYS.map(() => 0);
    (userCreatedEvents || []).forEach((event) => {
      const d = new Date(event.createdAt || event.startDate || Date.now());
      const day = d.getDay();
      const idx = day === 0 ? 6 : day - 1;
      buckets[idx] += event.totalRevenue || event.ticketsSold || 0;
    });
    return {
      labels: WEEK_DAYS,
      datasets: [
        {
          label: "Sales",
          data: buckets,
          backgroundColor: "#3B82F6",
          borderRadius: 4,
          borderSkipped: false,
        },
      ],
    };
  }, [stats.eventPerformance, userCreatedEvents]);

  const trafficChart = useMemo(
    () => ({
      labels: TRAFFIC_SOURCES.map((s) => s.name),
      datasets: [
        {
          data: TRAFFIC_SOURCES.map((s) => s.value),
          backgroundColor: TRAFFIC_SOURCES.map((s) => s.color),
          borderWidth: 0,
          spacing: 2,
        },
      ],
    }),
    []
  );

  const barOptions = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: { display: false },
        tooltip: {
          ...chartTooltipStyle,
          callbacks: {
            label: (ctx) => ` $${Number(ctx.raw || 0).toLocaleString()}`,
          },
        },
      },
      scales: {
        ...chartScales,
        y: {
          ...chartScales.y,
          beginAtZero: true,
        },
      },
    }),
    []
  );

  const doughnutOptions = {
    responsive: true,
    maintainAspectRatio: false,
    cutout: "62%",
    plugins: {
      legend: { display: false },
      tooltip: { ...chartTooltipStyle },
    },
  };

  const tableEvents = useMemo(() => {
    const fromRedux = userCreatedEvents || [];
    if (fromRedux.length > 0) {
      return fromRedux.slice(0, 10).map((event) => ({
        id: event._id,
        name: event.eventName || event.name || "Unnamed Event",
        date: event.startDate || event.createdAt,
        status: (event.status || "active").toLowerCase(),
        sold: event.ticketsSold || 0,
        revenue: event.totalRevenue || event.revenue || 0,
      }));
    }
    return (stats.eventPerformance || []).slice(0, 10).map((event) => ({
      id: event.eventId,
      name: event.eventName || "Unnamed Event",
      date: null,
      status: "active",
      sold: event.ticketsSold || 0,
      revenue: event.revenue || 0,
    }));
  }, [userCreatedEvents, stats.eventPerformance]);

  const isLoading = loading || userCreatedEventsLoading;

  return (
    <div className="flex flex-col flex-1 min-h-0 overflow-y-auto">
      {/* Header — matches new-ui OrganizerDashboard */}
      <div className="border-b border-[#1E293B]/60 bg-[#060D1B] shrink-0">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 px-4 sm:px-6 lg:px-8 py-5 sm:py-6">
          <div>
            <h1 className="text-[22px] text-white" style={{ fontWeight: 800 }}>
              Dashboard
            </h1>
            <p className="text-white/30 text-[13px] mt-0.5">
              Welcome back! Here&apos;s your event performance overview.
            </p>
          </div>
          <div className="flex items-center gap-3 shrink-0">
            <div className="flex items-center gap-2 px-4 py-2 rounded-xl bg-white/[0.04] border border-white/[0.08] text-[12px] text-white/40">
              <Clock className="w-3.5 h-3.5" />
              Last 30 days
              <ChevronRight className="w-3 h-3 rotate-90" />
            </div>
            <Link
              to="/create-event/form"
              className="flex items-center gap-2 px-5 py-2.5 rounded-xl bg-blue-500 text-white text-[12px] hover:bg-blue-600 transition-colors shadow-lg shadow-blue-500/20"
              style={{ fontWeight: 700 }}
            >
              <Zap className="w-3.5 h-3.5" /> New Event
            </Link>
          </div>
        </div>
      </div>

      <div className="p-4 sm:p-6 lg:p-8">
        {/* KPI cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          {kpis.map((kpi) => (
            <KpiCard key={kpi.label} kpi={kpi} loading={isLoading} />
          ))}
        </div>

        {/* Charts row */}
        <div className="grid grid-cols-1 xl:grid-cols-3 gap-4 mb-8">
          <div className="xl:col-span-2 p-6 rounded-2xl bg-[#0A1628] border border-[#1E293B]/60">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-[16px] text-white" style={{ fontWeight: 700 }}>
                  Revenue Overview
                </h2>
                <p className="text-[11px] text-white/25 mt-0.5">Monthly earnings from all events</p>
              </div>
              <div className="flex items-center gap-4 text-[10px] text-white/30">
                <span className="flex items-center gap-1.5">
                  <span className="w-2 h-2 rounded-full bg-blue-500" /> Revenue
                </span>
              </div>
            </div>
            <div className="h-[260px] min-h-[260px]">
              {isLoading ? (
                <div className="h-full flex items-center justify-center">
                  <div className="w-10 h-10 rounded-full border-2 border-blue-500/30 border-t-blue-500 animate-spin" />
                </div>
              ) : hasOverviewChartData ? (
                <Line
                  key={chartMonthKey(chartMonths)}
                  options={lineOptions}
                  data={revenueChartData.data}
                />
              ) : (
                <div className="h-full flex items-center justify-center text-white/30 text-[13px]">
                  No revenue data yet
                </div>
              )}
            </div>
          </div>

          <div className="p-6 rounded-2xl bg-[#0A1628] border border-[#1E293B]/60">
            <h2 className="text-[16px] text-white mb-1" style={{ fontWeight: 700 }}>
              Traffic Sources
            </h2>
            <p className="text-[11px] text-white/25 mb-4">Where your visitors come from</p>
            <div className="flex justify-center mb-4 h-[160px]">
              <Doughnut data={trafficChart} options={doughnutOptions} />
            </div>
            <div className="space-y-2.5">
              {TRAFFIC_SOURCES.map((s) => (
                <div key={s.name} className="flex items-center justify-between">
                  <div className="flex items-center gap-2.5">
                    <span className="w-2.5 h-2.5 rounded-sm" style={{ backgroundColor: s.color }} />
                    <span className="text-[12px] text-white/50" style={{ fontWeight: 500 }}>
                      {s.name}
                    </span>
                  </div>
                  <span className="text-[12px] text-white" style={{ fontWeight: 700 }}>
                    {s.value}%
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Second row */}
        <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
          <div className="p-6 rounded-2xl bg-[#0A1628] border border-[#1E293B]/60">
            <h2 className="text-[16px] text-white mb-1" style={{ fontWeight: 700 }}>
              This Week
            </h2>
            <p className="text-[11px] text-white/25 mb-4">Daily ticket sales</p>
            <div className="h-[200px]">
              <Bar options={barOptions} data={weeklySalesData} />
            </div>
          </div>

          <div className="xl:col-span-2 rounded-2xl bg-[#0A1628] border border-[#1E293B]/60 overflow-hidden">
            <div className="flex items-center justify-between px-6 py-5 border-b border-[#1E293B]/40">
              <div>
                <h2 className="text-[16px] text-white" style={{ fontWeight: 700 }}>
                  Recent Orders
                </h2>
                <p className="text-[11px] text-white/25 mt-0.5">Live feed of ticket purchases</p>
              </div>
              <button
                type="button"
                onClick={() => onNavigateTab?.("revenue")}
                className="text-[11px] text-blue-400 hover:text-blue-300 transition-colors"
                style={{ fontWeight: 600 }}
              >
                View all →
              </button>
            </div>
            <div>
              {tableEvents.length > 0 ? (
                tableEvents.slice(0, 5).map((event) => (
                  <div
                    key={event.id}
                    className="flex items-center justify-between px-6 py-3.5 border-b border-[#1E293B]/20 last:border-b-0 hover:bg-white/[0.02] transition-colors"
                  >
                    <div className="flex items-center gap-4 min-w-0">
                      <div className="w-8 h-8 rounded-lg bg-blue-500/10 flex items-center justify-center shrink-0">
                        <Ticket className="w-4 h-4 text-blue-400" />
                      </div>
                      <div className="min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="text-[13px] text-white truncate" style={{ fontWeight: 600 }}>
                            {event.name}
                          </span>
                        </div>
                        <div className="text-[11px] text-white/30">
                          {event.sold} tickets sold
                        </div>
                      </div>
                    </div>
                    <div className="text-right shrink-0 ml-4">
                      <div className="text-[14px] text-white" style={{ fontWeight: 700 }}>
                        {formatMoney(event.revenue)}
                      </div>
                      <div className="text-[10px] text-white/20">Event total</div>
                    </div>
                  </div>
                ))
              ) : (
                <div className="px-6 py-12 text-center text-white/30 text-[13px]">
                  Orders will appear here when fans purchase tickets
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Events table */}
        <div className="mt-8 rounded-2xl bg-[#0A1628] border border-[#1E293B]/60 overflow-hidden">
          <div className="flex items-center justify-between px-6 py-5 border-b border-[#1E293B]/40">
            <div>
              <h2 className="text-[16px] text-white" style={{ fontWeight: 700 }}>
                Your Events
              </h2>
              <p className="text-[11px] text-white/25 mt-0.5">Manage and track your active events</p>
            </div>
            <button
              type="button"
              onClick={() => onNavigateTab?.("events")}
              className="text-[11px] text-blue-400 hover:text-blue-300 transition-colors"
              style={{ fontWeight: 600 }}
            >
              View all →
            </button>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-[#1E293B]/30">
                  <th
                    className="text-left px-6 py-3.5 text-[10px] text-white/25 uppercase tracking-wider"
                    style={{ fontWeight: 700 }}
                  >
                    Event
                  </th>
                  <th
                    className="text-left px-6 py-3.5 text-[10px] text-white/25 uppercase tracking-wider"
                    style={{ fontWeight: 700 }}
                  >
                    Date
                  </th>
                  <th
                    className="text-left px-6 py-3.5 text-[10px] text-white/25 uppercase tracking-wider"
                    style={{ fontWeight: 700 }}
                  >
                    Status
                  </th>
                  <th
                    className="text-left px-6 py-3.5 text-[10px] text-white/25 uppercase tracking-wider"
                    style={{ fontWeight: 700 }}
                  >
                    Sold
                  </th>
                  <th
                    className="text-left px-6 py-3.5 text-[10px] text-white/25 uppercase tracking-wider"
                    style={{ fontWeight: 700 }}
                  >
                    Revenue
                  </th>
                  <th className="px-6 py-3.5" />
                </tr>
              </thead>
              <tbody>
                {tableEvents.length > 0 ? (
                  tableEvents.map((event) => {
                    const isPublished =
                      event.status === "active" ||
                      event.status === "published" ||
                      event.status === "live";
                    return (
                      <tr
                        key={event.id}
                        className="border-b border-[#1E293B]/15 last:border-b-0 hover:bg-white/[0.02] transition-colors"
                      >
                        <td className="px-6 py-4">
                          <span className="text-[13px] text-white" style={{ fontWeight: 600 }}>
                            {event.name}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-[12px] text-white/35">
                          {event.date
                            ? new Date(event.date).toLocaleDateString("en-US", {
                                month: "short",
                                day: "numeric",
                                year: "numeric",
                              })
                            : "—"}
                        </td>
                        <td className="px-6 py-4">
                          <span
                            className={`inline-flex items-center px-2.5 py-1 rounded-lg text-[10px] ${
                              isPublished
                                ? "bg-emerald-500/10 text-emerald-400"
                                : "bg-amber-500/10 text-amber-400"
                            }`}
                            style={{ fontWeight: 700 }}
                          >
                            {isPublished ? "Published" : "Draft"}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-[13px] text-white" style={{ fontWeight: 600 }}>
                          {event.sold}
                        </td>
                        <td className="px-6 py-4 text-[13px] text-white" style={{ fontWeight: 600 }}>
                          {formatMoney(event.revenue)}
                        </td>
                        <td className="px-6 py-4">
                          <button
                            type="button"
                            onClick={() => navigate(`/event/${event.id}`)}
                            className="p-2 hover:bg-white/5 rounded-lg transition-colors text-white/20 hover:text-white/50"
                            aria-label="Event options"
                          >
                            <MoreVertical className="w-4 h-4" />
                          </button>
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan={6} className="px-6 py-12 text-center text-white/30 text-[13px]">
                      No events yet.{" "}
                      <Link to="/create-event/form" className="text-blue-400 hover:underline">
                        Create your first event
                      </Link>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}
